import React, { useState } from 'react';
import studentService from '../../services/api/student';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getArabicDate } from '../../services/utils/date';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  MessageCircle, 
  Clock, 
  CheckCircle, 
  Search, 
  ChevronLeft, 
  BookOpen,
  User,
  HelpCircle
} from 'lucide-react';
const StudentQuestions = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // جلب أسئلة الطالب
  const { data: questions, isLoading } = useQuery(
    ['student-questions', searchTerm, filterStatus],
    () => studentService.getQuestions({
      search: searchTerm, 
      answered: filterStatus === 'all' ? undefined : filterStatus === 'answered'
    }),
    { 
      enabled: true, 
      keepPreviousData: true,
      refetchInterval: 30000, // Refresh every 30 seconds to check for new answers
      refetchOnWindowFocus: true
    }
  );

  if (isLoading && !questions) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تحميل أسئلتك...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <MessageCircle className="text-primary-600" size={32} />
                أسئلتي وإجاباتي
            </h1>
            <p className="text-gray-500 font-medium flex items-center gap-2">
                تابع جميع أسئلتك وإجابات المدرسين هنا
            </p>
          </div>
          
          <div className="bg-primary-600 text-white px-8 py-4 rounded-2xl shadow-xl shadow-primary-100 flex items-center gap-4">
              <div className="bg-white/20 p-2 rounded-xl"><MessageCircle size={24} /></div>
              <div>
                  <p className="text-[10px] font-black uppercase opacity-80 leading-none mb-1">إجمالي الأسئلة</p>
                  <p className="text-2xl font-black leading-none">{questions?.questions?.length || 0}</p>
              </div>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { label: 'أسئلة بانتظار الرد', value: questions?.questions?.filter(q => !q.answered_at).length || 0, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', urgent: true },
          { label: 'أسئلة تمت الإجابة', value: questions?.questions?.filter(q => q.answered_at).length || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'الدروس المتفاعلة', value: new Set(questions?.questions?.map(q => q.lesson_title)).size || 0, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-50' }
        ].map((stat, i) => (
          <div key={i} className={`bg-white p-6 rounded-[2rem] border shadow-sm flex items-center gap-5 group hover:shadow-md transition-all ${stat.urgent && stat.value > 0 ? 'border-amber-200 ring-4 ring-amber-50' : 'border-gray-100'}`}>
            <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>
              <stat.icon size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">{stat.label}</p>
              <p className="text-2xl font-black text-gray-900 leading-none">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 flex flex-col lg:flex-row items-center gap-4">
        <div className="relative flex-1 group w-full">
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />
          <input
            type="text"
            placeholder="ابحث عن سؤال محدد..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-12 pl-4 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
          />
        </div>
        
        <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-56">
                <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                <option value="all">كل الحالات</option>
                <option value="answered">تمت الإجابة</option>
                <option value="unanswered">لم تتم الإجابة</option>
                </select>
                <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90 pointer-events-none" size={16} />
            </div>
        </div>
      </div>

      {/* Questions Content List */}
      <div className="space-y-6 pb-20">
        {questions?.questions?.length === 0 ? (
          <div className="bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200 shadow-sm">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <HelpCircle size={48} className="text-gray-200" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">لا توجد أسئلة حالياً</h3>
            <p className="text-gray-500 max-w-sm mx-auto font-medium">لم تقم بطرح أي أسئلة بعد. اذهب إلى الدروس وابدأ بالتفاعل مع المدرسين.</p>
            <button
              onClick={() => navigate('/student/courses')}
              className="mt-6 bg-primary-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-primary-700 transition-all"
            >
              استكشاف الدروس
            </button>
          </div>
        ) : (
          questions?.questions?.map((question) => (
            <div 
                key={question.id} 
                className={`group bg-white rounded-[2.5rem] border transition-all duration-500 p-8 shadow-sm hover:shadow-xl ${!question.answered_at ? 'border-amber-100' : 'border-gray-100'}`}
            >
              {/* Question Header */}
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
                <div className="flex items-center gap-5">
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-black text-2xl shadow-lg shadow-primary-100 group-hover:scale-105 transition-transform overflow-hidden">
                        {question.student_name?.charAt(0).toUpperCase()}
                    </div>
                    {!question.answered_at && (
                        <div className="absolute -top-1 -left-1 w-4 h-4 bg-amber-500 rounded-full border-4 border-white animate-pulse"></div>
                    )}
                  </div>
                  <div>
                    <h4 className="text-lg font-black text-gray-900 leading-none mb-1.5 group-hover:text-primary-600 transition-colors">سؤالي</h4>
                    <div className="flex items-center gap-1 text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                        <User size={12} className="text-primary-400" /> {question.student_name}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50 px-4 py-2 rounded-xl">
                  <Clock size={16} className="text-primary-500" />
                  {getArabicDate(question.created_at)}
                </div>
              </div>

              {/* Context Info */}
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="bg-primary-50 text-primary-600 px-3 py-1 rounded-lg text-[10px] font-black border border-primary-100 uppercase tracking-widest flex items-center gap-1.5">
                    <BookOpen size={12} /> {question.course_title}
                </span>
                <ChevronLeft size={14} className="text-gray-300" />
                <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded-lg text-[10px] font-black border border-gray-200 uppercase tracking-widest">
                    {question.lesson_title}
                </span>
              </div>

              {/* Question Text */}
              <div className="bg-gray-50/50 rounded-[2rem] p-6 lg:p-8 border border-gray-50 relative mb-8">
                  <div className="absolute -top-4 right-6 bg-white px-4 py-1 rounded-full border border-gray-100 text-[10px] font-black text-gray-400 uppercase tracking-widest">نص السؤال</div>
                  <p className="text-gray-700 leading-relaxed text-lg font-medium italic">
                    "{question.text}"
                  </p>
              </div>

              {/* Answer Content */}
              {question.answered_at ? (
                <div className="animate-in slide-in-from-bottom duration-500">
                    <div className="bg-emerald-50 border border-emerald-100 rounded-[2rem] p-6 lg:p-8 relative overflow-hidden group/reply">
                    <div className="absolute top-0 right-0 w-1.5 h-full bg-emerald-500 opacity-20 group-hover/reply:opacity-100 transition-opacity"></div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <div className="bg-emerald-100 p-1.5 rounded-lg text-emerald-600">
                                <CheckCircle size={16} />
                            </div>
                            <span className="text-[10px] font-black text-emerald-800 uppercase tracking-widest">إجابة المدرس</span>
                        </div>
                        <span className="text-[9px] font-bold text-emerald-500 uppercase">{getArabicDate(question.answered_at)}</span>
                    </div>
                    <p className="text-emerald-900 leading-relaxed font-bold">{question.answer_text}</p>
                    </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-flex items-center gap-2 text-amber-600 bg-amber-50 px-6 py-3 rounded-2xl border border-amber-100">
                    <Clock size={20} />
                    <span className="font-bold">بانتظار رد المدرس...</span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StudentQuestions;
